import { Button, DropdownButton, DropdownMenuItem } from "@mixitone/components";
import EditIcon from "@mixitone/icons/edit.svg";
import TrashIcon from "@mixitone/icons/trash.svg";
import type { TournamentPaymentMethod } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import type { List } from "@mixitone/oom";
import ClubAkahuSetupBanner from "./ClubAkahuSetupBanner";
import { TournamentSessionFormController } from "./TournamentSessionForm";

interface PaymentMethodsSectionProps {
  paymentMethods: List<TournamentPaymentMethod>;
}

function formatPaymentAmount(method: TournamentPaymentMethod): string | null {
  const config = (method.configuration ?? {}) as Record<string, unknown>;
  const rawAmount = config.amount;
  const amount = typeof rawAmount === "number" ? rawAmount : Number(rawAmount);
  if (!Number.isFinite(amount)) {
    return null;
  }

  const currency =
    typeof config.currency === "string" && config.currency.trim().length > 0
      ? config.currency.trim().toUpperCase()
      : "NZD";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({ paymentMethods }) => {
  const controller = TournamentSessionFormController.use();
  const availablePaymentMethods = controller.getAvailablePaymentMethods();
  const { clubAkahuConnection, loadingClubAkahuConnection, startingAkahuOnboarding } = controller.state;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-medium whitespace-nowrap">Payment Methods</h3>
        <DropdownButton
          buttonProps={{
            kind: "select",
            className: "w-64",
          }}
          button="Add payment method"
        >
          {availablePaymentMethods.map((method) => (
            <DropdownMenuItem
              key={method.id}
              closeOnClick
              onClick={() => controller.actionShowAddPaymentMethodModal(method.id)}
            >
              {method.name}
            </DropdownMenuItem>
          ))}
        </DropdownButton>
      </div>

      {controller.hasAkahuPaymentMethod && controller.needsAkahuConnection && (
        <ClubAkahuSetupBanner
          connection={clubAkahuConnection}
          loading={loadingClubAkahuConnection}
          connecting={startingAkahuOnboarding}
          onConnect={() => controller.actionStartAkahuOnboarding()}
        />
      )}

      {paymentMethods.length === 0 ? (
        <div className="italic text-gray-500">No payment methods configured</div>
      ) : (
        <div className="space-y-2">
          {paymentMethods.map((method) => {
            const amountLabel = formatPaymentAmount(method);
            return (
              <div key={method.id} className="flex items-center justify-between rounded border p-3">
                <div>
                  <div className="font-medium">{method.description || method.payment_type}</div>
                  {amountLabel ? (
                    <div className="mt-0.5 text-sm text-gray-600">{amountLabel}</div>
                  ) : (
                    <div className="mt-0.5 text-sm text-amber-700">Amount not set</div>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button
                    kind="plain"
                    onClick={() => controller.actionShowEditPaymentMethodModal(method.id!)}
                    title="Edit payment method"
                  >
                    <EditIcon className="h-5 w-5 stroke-brand-700" />
                  </Button>
                  <Button
                    kind="plain"
                    onClick={() => controller.actionRemovePaymentMethod(method.id!)}
                    title="Remove payment method"
                  >
                    <TrashIcon className="h-5 w-5 text-red-800" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplicationView(PaymentMethodsSection);
